// External libraries
import React from 'react';
import ReactDOM from 'react-dom';
import { StoreProvider } from 'easy-peasy';
import { ApolloProvider } from '@apollo/react-hooks';
import { isFunction } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { apolloClient } from '@/lib/apollo';
import { App } from './components/App';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  features,
  getGlobals,
  isLiteEligible,
  isProd,
  redirectToXoon,
} from '@/lib';
import {
  $logger,
  IS_ADDRESSLESS_PAYLOAD,
  PAGE_LOAD_PAYLOAD,
  PREPARE_BILLING_PAYLOAD,
} from '@/lib/logger';

const {
  isInternalRequest,
  uri: { loggerEndpoint },
  user: { ecToken: token },
} = getGlobals();

// Initializations
$logger.init({
  loggerEndpoint,
  mode: features.isActive('isGuestEligible') ? 'guest' : 'signup',
  token,
});

/**
 * @TECH_DEBT @FIX_ME
 * This is a temporary hack to enable ramping in Japan. We need `unorm` for
 * legacy browsers in Japan so we can normalize strings but it is a rather
 * large poylfill that we don't want to load unless necessary. Long term we
 * should go back to using browser specific bundles but this is going to
 * require a bit of debugging to fix issues we were having when we first
 * implemented that.
 */
if (!isFunction(String.prototype.normalize)) {
  $logger.info('polyfilling_string_prototype_normalize');
  const { ui: uiActions } = appStore.getActions();

  // Toggle the page loading so that the user doesn't interact with
  // the form until the polyfill is available. The GQL data calls will
  // still be kicked off in the background.
  uiActions.toggleIsPageLoading(true);
  import('unorm')
    .then(() => {
      const normalize = typeof String.prototype.normalize;
      $logger.info('unorm_polyfill_successfully_loaded', { normalize });
      uiActions.toggleIsPageLoading(false);
    })
    .catch(err => {
      $logger.warn('unorm_polyfill_fetch_failed', err);
      redirectToXoon();
    });
}

(() => {
  // Client-side check for flow eligibility
  if (!isLiteEligible()) {
    return redirectToXoon();
  }

  ReactDOM.render(
    <ErrorBoundary>
      <ApolloProvider client={apolloClient}>
        <StoreProvider store={appStore}>
          <App />
        </StoreProvider>
      </ApolloProvider>
    </ErrorBoundary>,
    document.getElementById('root'),
  );

  const isAddressless = features.isActive('isAddressless');
  const isGuestEligible = features.someActive(
    'isGuestEligible',
    'isPOMAEligible',
  );

  $logger.fpti('page_interactive', PAGE_LOAD_PAYLOAD(), true);
  $logger.fpti(
    'prepare_billing_page',
    PREPARE_BILLING_PAYLOAD(isGuestEligible),
  );
  $logger.fpti('is_addressless', IS_ADDRESSLESS_PAYLOAD(isAddressless));

  // Remove initial data object from dom and window if we are in prod
  // and the request isn't internal traffic
  if (isProd && !isInternalRequest) {
    delete window.__INITIAL_DATA__;
    const dataScriptEl = document.getElementById('init-data-script');
    if (dataScriptEl) {
      dataScriptEl.parentElement.removeChild(dataScriptEl);
    }
  }
})();
